/**
 * @Description 面包屑
 */
import { routersConfig } from '@/router/router';
import { gatherMethods, isArray, matchQuery, recursionEach, treeFind } from '@lhb/func';
import { FC, useEffect, useMemo, useRef } from 'react';
import { Breadcrumb as AntdBreadcrumb } from 'antd';
import { Link } from 'react-router-dom';
import { getKeys } from '@/common/utils/ways';
import { UserInfoModuleList } from '@/common/api/brief';
import { dispatchNavigate } from '@/common/document-event/dispatch';
import { componentDocsMenu } from '@/layout/componentMenuConfig';

const routeList = routersConfig(); // 不能在jsx内执行，需要在Main函数之前就调用，才能获取到正确的页面组件

/** 路由白名单 */
const whiteList = [
  '/recommend/networkmap',
  '/recommend/detail',
  /^\/demo/,
  /^\/iframe/,
].map(item => item instanceof RegExp ? item : new RegExp(item, 'gi'));

const aliasUrl = [
  '/iterate/siteselectionmapb',
];
const Breadcrumb:FC<any> = ({
  location,
  userInfo,
  setPageConfig,
  selectKey,
  setSelectKey,
  setHavePermission,
  // getMenuMap
}) => {
  const isInitRef = useRef<any>(true);
  // 处理面包屑
  const handleBreadcrumbs = (result, matchItem) => {
    const allMenuList:any = [];// 所有菜单
    recursionEach(userInfo.moduleList, 'children', (item) => {
      allMenuList.push(item);
    });
    const breadcrumbInfo:any = [];
    parentMenu(allMenuList, matchItem, breadcrumbInfo);
    result.breadcrumbs = breadcrumbInfo;
    return result;
  };
  // 递归获取所有父级的name和uri，直到父级不存在
  const parentMenu = (allMenuList, matchItem, breadcrumbInfo) => {
    breadcrumbInfo.unshift({
      name: matchItem.name,
      path: matchItem.uri,
      click: false
    });
    if (matchItem.parent) {
      const cur = allMenuList.find((item) => item.uri === matchItem.parent.uri);
      parentMenu(allMenuList, cur, breadcrumbInfo);
    }
  };
  /** 组件面包屑处理 */
  const handleComponentsDocBreadcrumbs = (menuList, targetPath, parents: any = []) => {
    for (const item of menuList) {
      if (item.path === targetPath) {
        return [...parents, { name: item.name, path: item.path, click: false }];
      }
      if (Array.isArray(item.children) && item.children.length) {
        const found = handleComponentsDocBreadcrumbs(item.children, targetPath, [...parents, { name: item.name, path: item.path, click: true }]);
        if (found) return found;
      }
    }
    return null;
  };

  // 获取到各页面page.config.ts里的meta值
  const meta = useMemo(() => {
    const { search } = location.to;
    let { pathname } = location.to;

    // /iframe 页面比较特殊，产品可能配置路由 pathname=/iframe?url=xxx#hash，如果在 menus 中通过完整路由匹配到了，就使用完整路由作为选中 key
    if (pathname === '/iframe') {
      pathname = `${pathname}${location.to.search || ''}${location.to.hash || ''}`;
    }

    const metaName = matchQuery(search, 'metaName');
    const targetRoute = routeList?.find((item) => {
      return item.path === pathname;
    });

    let result = targetRoute?.meta || {};
    if (metaName) {
      result.name = metaName;
    }
    // meta.title取菜单名
    if (!userInfo.moduleList?.length) return {};

    const matchItem = treeFind(userInfo.moduleList || [], (item) => item.uri === pathname);

    // 匹配上了才赋值给meta.title
    if (matchItem?.name) {
      result.title = matchItem.name;
      result = handleBreadcrumbs(result, matchItem);
    } else if (result.isComponentDoc) {
      // 特殊场景1: 组件文档
      result.breadcrumbs = handleComponentsDocBreadcrumbs(componentDocsMenu, pathname) || [];
    } else {
      // 特殊场景2：匹配不上，说明是未配置的菜单（详情、历史页面）
      const parentPath = result?.parentPath?.[0] || location?.from?.pathname;
      const parentPage = treeFind(userInfo.moduleList || [], (item) => item.uri === parentPath);
      if (!parentPage) return {};
      // 这里result的最后一项是可点击的，且是进入该页面前的上一页页面的配置
      result = handleBreadcrumbs(result, parentPage);
      result.breadcrumbs[result.breadcrumbs.length - 1].click = true;
      // 将该页面放到面包屑的最后一项
      result.breadcrumbs.push({
        name: result.title,
        path: result?.url,
        click: false
      });
    }
    return result;
  }, [location.to, userInfo]);


  // 初始化、刷新，判断路由是否可用
  // 逻辑复用于src/layout/index.tsx
  const initRoute = () => {
    const { moduleList } = userInfo;
    const currentPath = location.to.pathname;
    if (!isArray(moduleList) || !moduleList?.length) return;
    // 处理别名url逻辑
    if (aliasUrl.includes(currentPath)) {
      const target = moduleList?.find((item) => item?.simpleUri === currentPath);
      if (target && target.uri) {
        dispatchNavigate(target.uri, { replace: true });
        return;
      }
    }
    // pathname==='/'时，需要跳转到第一个页面，且此时meta匹配不上，所以这里需要判断
    if (location.to.pathname !== '/' && (!Object.keys(meta)?.length) && !whiteList.some(pattern => currentPath.match(pattern))) {
      const target = (moduleList[0] as any).children.length ? (moduleList[0] as any).children[0] : moduleList[0];
      target && dispatchNavigate(target.uri);
      target && selectKey.length && selectKey[0] !== target.uri && setSelectKey(target.uri);
      return;
    }
    if (!isInitRef.current) return;
    isInitRef.current = false;
    const allUri = getKeys(moduleList as UserInfoModuleList[], [], 'uri', 'children', true);
    const collectionPath = gatherMethods(meta?.parentPath || [], allUri, 1);
    const isIN = allUri.includes(currentPath) || collectionPath.length; // 是否可访问
    // 如果当前页面不在菜单列表中且子菜单所属的父级菜单也不在菜单列表中则默认跳转到第一个页面
    if (!isIN && moduleList && moduleList.length && !whiteList.some(pattern => currentPath.match(pattern))) {
      const target = (moduleList[0] as any).children.length ? (moduleList[0] as any).children[0] : moduleList[0];
      target && dispatchNavigate(target.uri);
      target && selectKey.length && selectKey[0] !== target.uri && setSelectKey(target.uri);
    }
    // 如果没有菜单权限-且不在404页面则页面全局提示暂无权限
    if (moduleList && !moduleList.length && currentPath !== '/404') {
      setHavePermission(false);
    }
  };
  useEffect(() => {
    setPageConfig(meta);
    initRoute();
  }, [meta]);

  return <AntdBreadcrumb>
    {meta?.breadcrumbs &&
    meta.breadcrumbs.map((item, index) => (
      <AntdBreadcrumb.Item key={index}>
        {
          item?.click
            ? <Link to={item.path}>{item.name}</Link>
            : item.name
        }
      </AntdBreadcrumb.Item>
    ))}
  </AntdBreadcrumb>;
};
export default Breadcrumb;
